import type { PaymentMode } from '@/types/misc';

// region Payment Modes
export const PAYMENT_MODE_PAYPAL = 'payPal';
export const PAYMENT_MODE_INVOICE = 'invoice';
export const PAYMENT_MODE_BANK = 'bank';
export const PAYMENT_MODE_CREDITCARD = 'creditcard';
export const PAYMENT_MODE_ADVANCE = 'advance';
export const PAYMENT_MODE_CASHONDELIVERY = 'cashOnDelivery';
export const PAYMENT_MODE_ADYEN_PREFIX = 'adyen_';
export const PAYMENT_MODE_ADYEN_BANCONTACT = 'adyen_bancontact';
export const PAYMENT_MODE_ADYEN_ONLINEBANKING = 'adyen_p24';
export const PAYMENT_MODE_ADYEN_PAYCONIQ = 'adyen_payconiq';
export const PAYMENT_MODE_ADYEN_CREDITCARD = 'adyen_creditcard';
export const PAYMENT_MODE_ADYEN_TWINT = 'adyen_twint';
export const PAYMENT_MODE_ADYEN_MOBILEPAY = 'adyen_mobilepay';
export const PAYMENT_MODE_ADYEN_BLIK = 'adyen_blik';
export const PAYMENT_MODE_ADYEN_APPLEPAY = 'adyen_applepay';
export const PAYMENT_MODE_ADYEN_GOOGLEPAY = 'adyen_googlepay';
export const PAYMENT_MODE_ADYEN_BCMC_MOBILE = 'bcmc_mobile';
// endregion

// region Adyen
export const ADYEN_COMPONENT_CARD = 'Card';
export const ADYEN_COMPONENT_BANCONTACT = 'Bancontact';
export const ADYEN_COMPONENT_BLIK = 'Blik';
export const ADYEN_COMPONENT_ONLINEBANKING_PL = 'OnlineBankingPL';
export const ADYEN_COMPONENT_APPLEPAY = 'ApplePay';
export const ADYEN_COMPONENT_GOOGLEPAY = 'GooglePay';
export const ADYEN_ACTION_QRCODE = 'qrCode';
export const ADYEN_ACTION_3DS = 'threeDS2';
// endregion

export const DEVICE_ELIGIBILITY_COOKIE_MAX_AGE = 60 * 60;

export const getAfterpayConfig = (config: any) => ({
  fingerprinting: {
    script: config.public.afterpayFingerprintingScript,
    clientId: config.public.afterpayFingerprintingClientId,
  },
  directDebit: {
    script: config.public.afterpayDirectDebitScript,
    baseAddress: config.public.afterpayDirectDebitBaseAddress,
  },
});

export const getPaypalConfig = (config: any) => ({
  fraudnet: {
    script: config.public.paypalFraudnetScript,
    image: `${config.public.paypalFraudnetImage}&s=${config.public.paypalFraudnetIdentifier}`,
    identifier: config.public.paypalFraudnetIdentifier,
  },
});

export const getAdyenConfig = (config: RuntimeConfig) => ({
  clientKey: config.public.adyenClientkey,
  environment: config.public.adyenEnvironment,
});

export const paymentRedirect = ({
  method,
  url = '',
  params = [],
}: {
  method: string;
  url: string;
  params: Array<{ key: string; value: string }>;
}) => {
  if (method === 'POST') {
    const form = document.createElement('form');
    form.setAttribute('method', 'POST');
    form.setAttribute('action', url);
    form.setAttribute('target', '_self');

    for (const entry of params) {
      const hiddenField = document.createElement('input');
      hiddenField.setAttribute('type', 'hidden');
      hiddenField.setAttribute('name', entry.key);
      hiddenField.setAttribute('value', entry.value);
      form.appendChild(hiddenField);
    }

    document.body.appendChild(form);
    form.submit();
    return;
  }

  window.location.href = url;
};

export const paymentErrorRedirect = async (error: any) => {
  await timeout(2000);

  const singleError = error?.response?.data?.errors?.length === 1 ? error.response.data.errors[0] : null;

  switch (singleError?.subject?.toLowerCase()) {
    case 'refused':
      await navigateTo('/checkout/payment/?refused=true');
      break;

    case 'cancelled':
      await navigateTo('/checkout/payment/?cancelled=true');
      break;

    default:
      await navigateTo('/checkout/payment/?error=true');
      break;
  }
};

export const isAfterpay = (paymentMode: string) => {
  return [PAYMENT_MODE_INVOICE, PAYMENT_MODE_BANK].includes(paymentMode);
};

export const isAdyenWithUserInputEncrypted = (paymentMode: string) => {
  return [PAYMENT_MODE_ADYEN_CREDITCARD, PAYMENT_MODE_ADYEN_BANCONTACT].includes(paymentMode);
};

export const isAdyenWithUserInput = (paymentMode: string) => {
  return isAdyenWithUserInputEncrypted(paymentMode) || [PAYMENT_MODE_ADYEN_ONLINEBANKING].includes(paymentMode);
};

export const checkIsPaymentStateInvalid = (paymentMode: string, birthday?: string, fullPaybackPayment?: boolean) => {
  const hasMissingBankData = paymentMode === PAYMENT_MODE_BANK && !unref(useCookie('cart-payment'));
  const hasMissingCreditCardData = isAdyenWithUserInput(paymentMode) && !readAdyenPayload();
  const hasMissingMinAge = paymentMode === PAYMENT_MODE_INVOICE && !birthday && !fullPaybackPayment;

  return !fullPaybackPayment && (hasMissingBankData || hasMissingCreditCardData || hasMissingMinAge);
};

export const isPaymentModeIncluded = (paymentModes: Array<PaymentMode>, paymentModeCode: string) => {
  if (!paymentModes.length) {
    return false;
  }
  return paymentModes.some((mode: PaymentMode) => mode.code === paymentModeCode);
};

export const getValidPaymentModes = (paymentModes: Array<PaymentMode>) => {
  const { canMakeApplePayPayments, canMakeGooglePayPayments } = useCartStore();

  return paymentModes.filter((mode: PaymentMode) => {
    if (!canMakeApplePayPayments && mode?.code === PAYMENT_MODE_ADYEN_APPLEPAY && !mode.selected) {
      return false;
    }

    if (!canMakeGooglePayPayments && mode?.code === PAYMENT_MODE_ADYEN_GOOGLEPAY && !mode.selected) {
      return false;
    }

    return true;
  });
};

export const checkAppleAndGooglePayEligibility = async () => {
  getApplePayGooglePayEligibilityFromCookie();

  if (import.meta.server) {
    return;
  }

  const cartStore = useCartStore();
  const { configuration } = useMainStore();
  const { paymentModes } = configuration;

  const hasApplePay = isPaymentModeIncluded(paymentModes, PAYMENT_MODE_ADYEN_APPLEPAY);
  const hasGooglePay = isPaymentModeIncluded(paymentModes, PAYMENT_MODE_ADYEN_GOOGLEPAY);
  const googlePayMerchantId = paymentModes.find(
    (mode: PaymentMode) => mode.code === PAYMENT_MODE_ADYEN_GOOGLEPAY,
  )?.merchantId;

  const applePayEligibility = hasApplePay ? !!canMakeApplePayPayments() : null;
  const googlePayEligibility =
    hasGooglePay && googlePayMerchantId ? !!(await canMakeGooglePayPayments(googlePayMerchantId)) : null;

  cartStore.canMakeApplePayPayments = applePayEligibility;
  cartStore.canMakeGooglePayPayments = googlePayEligibility;

  useCookie('canMakeApplePayPayments', {
    maxAge: DEVICE_ELIGIBILITY_COOKIE_MAX_AGE,
  }).value = applePayEligibility?.toString();

  useCookie('canMakeGooglePayPayments', {
    maxAge: DEVICE_ELIGIBILITY_COOKIE_MAX_AGE,
  }).value = googlePayEligibility?.toString();
};

export const getApplePayGooglePayEligibilityFromCookie = () => {
  const cartStore = useCartStore();
  const cookieApple = unref(useCookie('canMakeApplePayPayments'));
  const cookieGoogle = unref(useCookie('canMakeGooglePayPayments'));

  cartStore.canMakeApplePayPayments = typeof cookieApple === 'boolean' ? cookieApple : null;
  cartStore.canMakeGooglePayPayments = typeof cookieGoogle === 'boolean' ? cookieGoogle : null;
};

export const canMakeApplePayPayments = () => {
  return import.meta.client && window.ApplePaySession?.canMakePayments();
};

export const canMakeGooglePayPayments = async (merchantId: string) => {
  const { googlePayEnvironment } = useNuxtApp().$config.public;
  const { isAppview } = useMainStore();

  if (!googlePayEnvironment || (isAppview && !webViewCanMakePayments())) {
    return false;
  }

  try {
    const googlePaymentsApi = await repeatUntil(() => window.google?.payments?.api);

    const paymentsClient = new googlePaymentsApi.PaymentsClient({
      environment: googlePayEnvironment,
      merchantInfo: merchantId,
    });

    const { result: canMakePayments } = await paymentsClient.isReadyToPay({
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: ['MASTERCARD', 'VISA', 'MAESTRO'],
          },
        },
      ],
    });

    return canMakePayments;
  } catch (error) {
    logger.warn('Could not load GooglePay script.');
    return false;
  }
};

const webViewCanMakePayments = () => {
  // First array element should be the version number from UA. For instance: Chrome/132.
  const [, chromeVersion] = navigator?.userAgent?.match(/Chrome\/(\d+)\./) || [];
  if (!chromeVersion) {
    return false;
  }

  const currentVersion = parseInt(chromeVersion, 10);
  const minVersion = parseInt(useNuxtApp().$config.public.googlePayWebViewSupportedFromVersion, 10);
  const isSupported = currentVersion && currentVersion >= minVersion;

  return window.PaymentRequest && isSupported;
};
